import {
    forwardRef,
    useEffect,
    useState,
    useMemo
} from "react";
import {
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IToggleProps from "./type";
import type {
    ToggleNativePropsType,
    ToggleIconDirection,
    ToggleWebPropsType
} from "./type";
import stylesheet, {
    getToggleIconDirection,
    getToggleColors,
    getToggleSize,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    getCrossfadeStyle,
    useAnimatedState,
    useReducedMotion,
    usePressScale
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    useAttachedGroupItem
} from "../attachedGroup/context";
import {
    getAttachedGroupSquircleCorners,
    getAttachedGroupLayerCorners,
    getAttachedGroupItemStyle,
    getAttachedGroupCorners
} from "../attachedGroup/stylesheet";
import {
    splitControlStyle
} from "../../helpers/theme/control";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    warnOnce
} from "../../utils/development";
import StateLayer from "../stateLayer";
import Text from "../text";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const Toggle = forwardRef<View, IToggleProps>(({
    activeIcon: ActiveIconComponentProp,
    isDisabled: isDisabledProp = false,
    variant: variantProp = "ghost",
    isPressScaleEnabled = true,
    size: sizeProp = "medium",
    isDefaultPressed = false,
    isPressed: isPressedProp,
    "aria-label": ariaLabel,
    icon: IconComponentProp,
    iconDirection = "start",
    accessibilityLabel,
    isIconOnly = false,
    onPressedChange,
    customTheme,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onBlur,
    style,
    title,
    ...props
}, ref) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const groupItem = useAttachedGroupItem();

    const [
        isPressedState,
        setIsPressedState
    ] = useState<boolean>(isDefaultPressed);

    const isControlled = isPressedProp !== undefined;

    const isToggledOn = isControlled ? isPressedProp : isPressedState;

    const isDisabled = isDisabledProp || !!groupItem?.isDisabled;

    const isAttached = !!groupItem?.isAttached;

    const isVertical = groupItem?.orientation === "vertical";

    const size = groupItem?.size ?? sizeProp;

    const requestedVariant = groupItem?.toggleVariant ?? variantProp;

    const variant = isAttached ? "outline" : requestedVariant;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed,
        isFocused
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const pressScaleStyle = usePressScale({
        isEnabled: isPressScaleEnabled && !isAttached,
        customTheme,
        isDisabled,
        isPressed
    });

    const isReduceMotionEnabled = useReducedMotion();

    const iconSwapProgress = useAnimatedState({
        value: isToggledOn ? 1 : 0,
        intent: "iconSwap",
        motion: "fade",
        customTheme
    });

    const iconCrossfadeStyle = useMemo(() => getCrossfadeStyle({
        progress: iconSwapProgress,
        isReduceMotionEnabled
    }), [
        isReduceMotionEnabled,
        iconSwapProgress
    ]);

    const isIconOnlyToggle = isIconOnly && !!IconComponentProp;

    const currentIconDirection = getToggleIconDirection({
        iconDirection
    });

    const currentSize = getToggleSize({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const currentColors = getToggleColors({
        isHighlighted: isHovered || isPressed,
        isActive: isToggledOn,
        isDisabled,
        variant,
        colors
    });

    const {
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        borderRadius: radiuses.actions,
        isIconOnly: isIconOnlyToggle,
        isActive: isToggledOn,
        isFocusVisible,
        currentColors,
        shapeConfigs,
        touchTargets,
        currentSize,
        pointerType,
        shadowAlpha,
        elevations,
        isDisabled,
        variant,
        borders,
        colors,
        spaces
    });

    const attachedStyle = groupItem?.isAttached ? getAttachedGroupItemStyle({
        isFocused: isFocused && isFocusVisible,
        innerRadius: groupItem.innerRadius,
        outerRadius: groupItem.outerRadius,
        orientation: groupItem.orientation,
        isOverlap: groupItem.isOverlap,
        position: groupItem.position,
        isActive: isToggledOn,
        shapeConfigs,
        isDisabled,
        borders
    }) : undefined;

    const layerCorners = groupItem?.isAttached ? getAttachedGroupSquircleCorners({
        corners: getAttachedGroupLayerCorners({
            corners: getAttachedGroupCorners({
                innerRadius: groupItem.innerRadius,
                outerRadius: groupItem.outerRadius,
                orientation: groupItem.orientation,
                position: groupItem.position
            }),
            borders
        }),
        shapeConfigs
    }) : undefined;

    if(attachedStyle) {
        Object.assign(containerDynamicStyle, attachedStyle.container);

        Object.assign(surfaceDynamicStyle, attachedStyle.surface, {
            borderRadius: getCornerStyle({
                radius: groupItem?.outerRadius ?? radiuses.actions,
                shapeConfigs
            }).borderRadius
        });

        if(groupItem?.isShadowOwner) {
            delete surfaceDynamicStyle.boxShadow;
        }
    }

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    useEffect(() => {
        if(!isIconOnlyToggle || title || ariaLabel || accessibilityLabel) {
            return;
        }

        warnOnce({
            message: "Toggle: an icon-only toggle needs a \"title\" (or \"aria-label\") so screen readers can announce it."
        });
    }, [
        accessibilityLabel,
        isIconOnlyToggle,
        ariaLabel,
        title
    ]);

    useEffect(() => {
        if(!isAttached || requestedVariant !== "ghost") {
            return;
        }

        warnOnce({
            message: "Toggle: a \"ghost\" toggle has no edge to share inside an attached group, so it is drawn as \"outline\"."
        });
    }, [
        requestedVariant,
        isAttached
    ]);

    const handlePress = () => {
        const nextPressed = !isToggledOn;

        if(!isControlled) {
            setIsPressedState(nextPressed);
        }

        onPressedChange?.(nextPressed);
    };

    const renderIcon = (direction: ToggleIconDirection) => {
        if(currentIconDirection !== direction) {
            return null;
        }

        if(IconComponentProp && ActiveIconComponentProp) {
            return <View>
                <Animated.View
                    style={iconCrossfadeStyle.outgoing}
                    aria-hidden={isToggledOn}
                >
                    <IconComponentProp
                        customColor={currentColors.iconColor}
                        color={currentColors.iconColorKey}
                        size={currentSize.iconSize}
                    />
                </Animated.View>
                <Animated.View
                    style={[
                        stylesheet.activeIconContainer,
                        iconCrossfadeStyle.incoming
                    ]}
                    aria-hidden={!isToggledOn}
                >
                    <ActiveIconComponentProp
                        customColor={currentColors.iconColor}
                        color={currentColors.iconColorKey}
                        size={currentSize.iconSize}
                    />
                </Animated.View>
            </View>;
        }

        const CurrentIcon = isToggledOn && ActiveIconComponentProp ? ActiveIconComponentProp : IconComponentProp;

        if(!CurrentIcon) {
            return null;
        }

        return <CurrentIcon
            customColor={currentColors.iconColor}
            color={currentColors.iconColorKey}
            size={currentSize.iconSize}
        />;
    };

    const renderTitle = () => {
        if(!title || isIconOnlyToggle) {
            return null;
        }

        return <Text
            customColor={currentColors.titleColor}
            variant={currentSize.labelVariant}
            customTheme={customTheme}
            style={stylesheet.title}
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    const webProps: ToggleWebPropsType = Platform.OS === "web" ? {
        "aria-pressed": isToggledOn,
        role: "button"
    } : {};

    const nativeProps: ToggleNativePropsType = Platform.OS === "web" ? {} : {
        accessibilityRole: "togglebutton",
        accessibilityState: {
            disabled: isDisabled,
            checked: isToggledOn
        }
    };

    return <AnimatedPressable
        {...props}
        {...webProps}
        {...nativeProps}
        {...interactionHandlers}
        aria-label={ariaLabel ?? (isIconOnlyToggle ? title : undefined)}
        style={[
            containerStyle,
            stylesheet.container,
            (isVertical || isAttached) && stylesheet.stretchContainer,
            containerDynamicStyle,
            pressScaleStyle
        ]}
        accessibilityLabel={accessibilityLabel}
        aria-disabled={isDisabled}
        onPress={handlePress}
        disabled={isDisabled}
        ref={ref}
    >
        <View
            style={[
                surfaceStyle,
                stylesheet.surface,
                isAttached && stylesheet.fillSurface,
                isVertical && !isIconOnlyToggle && stylesheet.startAlign,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                borderRadius={groupItem?.isAttached ? groupItem.outerRadius : radiuses.actions}
                overlayColor={currentColors.overlayColor}
                borderWidth={borders.line}
                customTheme={customTheme}
                isHovered={isHovered}
                isPressed={isPressed}
                style={layerCorners}
                isSquircle={true}
            />
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderIcon("start")}
                {renderTitle()}
                {renderIcon("end")}
            </View>
        </View>
    </AnimatedPressable>;
});
Toggle.displayName = "Toggle";
export default Toggle;
